import { useState } from 'react'
import { Button, Column, Control, Icon, Image, Input } from 'rbx'
import { useFetch } from '../../../hooks/useFetch'
import { API_URL } from '../../../services/api'
import Loading from '../../../helpers/Loading'
import moment from 'moment'
import Controls from '../../shared/Controls/Controls'
import { Redirect } from 'react-router-dom'
import { RedirectTo } from '../../../utils/redirectTo'
import Select from 'react-select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle, faLayerGroup, faSearch, faSpinner } from '@fortawesome/free-solid-svg-icons'
import pinMonitorOnline from '../../../assets/pin-monitor-online.png'
import { ZadMap } from '../../shared/ZadMap'
import { MonitorsPointsLayer, MAX_RENDERED_POINTS } from './MonitorsPointsLayer'

const queryConfig = {
  date: moment().format('YYYY-MM-DD'),
  startTime: '08:00',
  endTime: '19:00'
}

// Aviso discreto sobre o mapa (truncamento / período vazio). Fica no fluxo do
// search-container, logo abaixo do formulário, para herdar posicionamento e zIndex.
const MapNotice = ({ icon, children }) => (
  <div
    role="status"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      background: 'white',
      border: '1px solid hsl(0deg, 0%, 86%)',
      borderRadius: '4px',
      padding: '0.4rem 0.75rem',
      marginLeft: '0.75rem',
      // --text-title (#363f5f) e não --text-body (#969cb3): o token de corpo rende
      // ~2.5:1 sobre branco e reprova no piso WCAG AA de 4.5:1.
      color: 'var(--text-title)',
      fontSize: '0.9rem'
    }}
  >
    <FontAwesomeIcon icon={icon} aria-hidden="true" />
    <span>{children}</span>
  </div>
)

export const FindMonitors = () => {
  const [monitor, setMonitor] = useState('')
  const [date, setDate] = useState(queryConfig.date)
  const [startTime, setStartTime] = useState(queryConfig.startTime)
  const [endTime, setEndTime] = useState(queryConfig.endTime)
  const [query, setQuery] = useState(queryConfig)
  const [redirectTo, setRedirectTo] = useState(null)

  const { 
    response: areasResponse,
    load: areaLoad
  } = useFetch(`${API_URL}/areas`)

  const areasMap = areasResponse?.data||[]

  const { response: monitoresData } = useFetch(`${API_URL}/adm/monitors?deleted=false`)

  const monitores = monitoresData?.data?.map((m) => ({ value: m.id, label: m.name }))

  const { 
    response: locationMonitorsResponse,
    load: locationMonitorsLoad,
    refresh: locationMonitorsRefresh
  } = useFetch(`${API_URL}/adm/location-history-monitors?${new URLSearchParams(query)}`, true)

  // console.log('FindMonitors', 'Rendered')

  if (redirectTo)
  return <Redirect to={{ pathname: redirectTo }} />

  const handleSubmit = (event) => {
    event.preventDefault()
    if (date && startTime && endTime) {
      if (date === query.date && startTime === query.startTime && endTime === query.endTime) {
        locationMonitorsRefresh()
      } else {
        setQuery({
          date,
          startTime,
          endTime
        })
      }
    }
  }

  const searchContainerStyle = {
    position: 'absolute',
    top: 6,
    left: 6,
    zIndex: 4,
    pointerEvents: 'auto',
    overflow: 'hidden',
    whiteSpace: 'normal',
    fontFamily: '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    width: '100%',
    maxWidth: '60rem'
  }

  const inputStyle = {
    background: 'white',
    padding: '1.5rem 0.5rem',
  }

  const allPaths = locationMonitorsResponse?.data?.paths || []
  const filteredPaths = monitor
    ? allPaths.filter((path) => path.userId === monitor.value)
    : allPaths

  const isTruncated = filteredPaths.length > MAX_RENDERED_POINTS

  // Chaveado na PRESENÇA da resposta, não em `!locationMonitorsLoad`: o useFetch
  // inicia `load` como '' (falsy), então `!load` também é verdadeiro antes do
  // primeiro fetch resolver e o aviso piscaria sem motivo. Cobre igualmente o caminho
  // de erro da API — em resposta não-200 o useFetch seta response = [] (truthy),
  // filteredPaths fica vazio e o usuário vê o aviso em vez de um mapa mudo.
  const isEmptyResult =
    locationMonitorsResponse && !locationMonitorsLoad && filteredPaths.length === 0

  return !areaLoad ? (
    <>
      <div className='search-container' style={searchContainerStyle}>
        <form onSubmit={handleSubmit}>
          <Column.Group gapSize={8}>
            <Column className='pb-0'>
              <Column.Group gapSize={1} breakpoint='mobile'>
                <Column>
                  <Control iconLeft >
                    <Input 
                      style={{ background: 'white', padding: '1.5rem 0.3rem 1.5rem 2.5rem', }}
                      name="date"
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                    <Icon className='is-clickable pt-2' align="left" onClick={() => setRedirectTo(RedirectTo.dashboard)}>
                      <Image src={pinMonitorOnline} title='Home' />
                    </Icon>
                  </Control>
                </Column>
                <Column className='is-one-third'>
                  <Controls.TextField 
                    style={inputStyle}
                    name="startTime"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </Column>
                <Column className='is-one-third'>
                  <Controls.TextField 
                    style={inputStyle}
                    name="endTime"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </Column>
              </Column.Group>
            </Column>

            <Column>
              <Column.Group gapSize={1} breakpoint='mobile'>
                <Column className=''>
                  <Select
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        padding: '0.3rem 0rem'
                      }),
                      menu: (baseStyles, state) => ({
                        ...baseStyles,
                        position: 'relative'
                      }),
                    }}
                    classNamePrefix="select"
                    defaultValue={monitores?.filter(m => m.value === monitor?.value)}
                    isDisabled={false}
                    isLoading={false}
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="monitor"
                    options={monitores}
                    onChange={(e) => setMonitor(e)}
                  />
                </Column>
                <Column className='is-one-fifth'>
                  <Button style={{ padding:'1.5rem 1rem' }}>
                    {
                      locationMonitorsLoad
                        ? <FontAwesomeIcon icon={faSpinner} pulse />
                        : <FontAwesomeIcon icon={faSearch} />
                    }
                  </Button>
                </Column>
              </Column.Group>
            </Column>
          </Column.Group>
        </form>

        {
          isEmptyResult && (
            <MapNotice icon={faInfoCircle}>
              Nenhum registro de localização no período selecionado.
            </MapNotice>
          )
        }

        {
          isTruncated && (
            <MapNotice icon={faLayerGroup}>
              {`Mostrando os primeiros ${MAX_RENDERED_POINTS.toLocaleString('pt-BR')} de ${filteredPaths.length.toLocaleString('pt-BR')} registros. Reduza o intervalo de horário ou selecione um monitor para ver o restante.`}
            </MapNotice>
          )
        }
      </div>

      <ZadMap areasMap={areasMap} mapContainerStyle={{ width: '100vw', height: '100vh' }} zoom={17}>
        <MonitorsPointsLayer points={filteredPaths} />
      </ZadMap>
    </>
  ) : <Loading />
}
